import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Copy, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { EditUserDialog } from './EditUserDialog';
import { ClientUserActionsDropdown } from './ClientUserActionsDropdown';

interface UserData {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  department: string;
  is_active: boolean;
  last_active: string | null;
  scored_30d: number;
  avg_score_30d: number;
  fsr: number;
}

interface UserManagementTabProps {
  orgId: string;
}

export function UserManagementTab({ orgId }: UserManagementTabProps) {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch users and invite code in parallel
      const [usersRes, codeRes] = await Promise.all([
        supabase.rpc('get_org_users_for_client_admin', { _org_id: orgId }),
        supabase.rpc('get_org_invite_code', { _org_id: orgId }),
      ]);

      if (usersRes.error) throw usersRes.error;
      setUsers(usersRes.data || []);

      if (!codeRes.error && codeRes.data) {
        setInviteCode(codeRes.data);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const copyInviteLink = async () => {
    if (!inviteCode) return;
    const signupUrl = `${window.location.origin}/signup?inviteCode=${encodeURIComponent(inviteCode)}`;
    try {
      await navigator.clipboard.writeText(signupUrl);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast.success('Invite link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Team Members</h2>
          <p className="text-sm text-muted-foreground">{users.length} users in your organization</p>
        </div>
        {inviteCode && (
          <Button variant="outline" size="sm" onClick={copyInviteLink}>
            {copiedCode ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copiedCode ? 'Copied!' : 'Copy Invite Link'}
          </Button>
        )}
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Submissions (30d)</TableHead>
                <TableHead>FSR</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                    No team members yet. Add your first user to get started.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell className="font-medium">{user.full_name || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{user.role}</Badge>
                    </TableCell>
                    <TableCell>{user.department}</TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.last_active 
                        ? formatDistanceToNow(new Date(user.last_active), { addSuffix: true }) 
                        : 'Never'}
                    </TableCell>
                    <TableCell>{user.scored_30d}</TableCell>
                    <TableCell>{user.fsr || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 items-center">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setEditingUser(user)}
                          disabled={user.role === 'client_admin' || user.role === 'admin'}
                        >
                          Edit
                        </Button>
                        <ClientUserActionsDropdown 
                          user={user} 
                          onRefresh={fetchData} 
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <EditUserDialog
        user={editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
        onSuccess={fetchData}
      />
    </div>
  );
}
